import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';

import { ORGANIZATION_PERMISSION_TYPE } from '@/enums';
import { OrganizationPermission, PaymentPlan, TableData } from '@/types';

const getOrganizationPermissionByName = (
  organizationPermissions: OrganizationPermission[],
  permissionName: ORGANIZATION_PERMISSION_TYPE
) => {
  return organizationPermissions.find((permission) => permission.name === permissionName);
};

export const getPaymentPlanColumns: () => ColumnDef<TableData>[] = () => [
  {
    id: 'name',
    accessorKey: 'name',
    header: i18n.t('name', { ns: 'common' }),
    cell: (info) => {
      const name = info.getValue() as string;
      const isDefault = (info.row.original as PaymentPlan).isDefault;

      return (
        <span className='flex items-center gap-2'>
          {name}
          {isDefault && (
            <>
              <span className='hidden h-[25px] items-center rounded-full bg-second-accent-light-green px-1.5 py-0.5 text-2xs font-medium !text-second-accent-green md:inline-flex'>
                <>{i18n.t('default', { ns: 'common' })}</>
              </span>
              <div className={'size-2 rounded-full bg-second-accent-light-green md:hidden'} />
            </>
          )}
        </span>
      );
    }
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: i18n.t('description', { ns: 'common' }),
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <div className='truncate md:max-w-[250px] lg:max-w-[350px]' title={value}>
          {value}
        </div>
      );
    }
  },
  {
    id: 'activeServices',
    accessorKey: 'activeServices',
    header: i18n.t('activeServices', { ns: 'common' }),
    cell: (props) => (
      <div className='flex justify-end md:justify-start'>{props.getValue() as string}</div>
    )
  }
];

export const isDigitalSignageSelected = (
  organizationPermissions: OrganizationPermission[],
  organizationPermissionIds: number[] = []
) => {
  const digitalSignagePermission = getOrganizationPermissionByName(
    organizationPermissions,
    ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE
  );

  return digitalSignagePermission
    ? organizationPermissionIds.includes(digitalSignagePermission.id)
    : false;
};

export const isMediaLibraryPermission = (permission: OrganizationPermission) => {
  return permission.name === ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY;
};

export const updateOrganizationPermissionIds = (
  organizationPermissions: OrganizationPermission[],
  currentPermissionIds: number[],
  permission: OrganizationPermission
) => {
  const isSelected = currentPermissionIds.includes(permission.id);

  let updatedPermissionIds = isSelected
    ? currentPermissionIds.filter((id) => id !== permission.id)
    : [...currentPermissionIds, permission.id];

  if (permission.name === ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE && !isSelected) {
    const mediaLibraryPermission = getOrganizationPermissionByName(
      organizationPermissions,
      ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY
    );

    if (mediaLibraryPermission && !updatedPermissionIds.includes(mediaLibraryPermission.id)) {
      updatedPermissionIds = [...updatedPermissionIds, mediaLibraryPermission.id];
    }
  }

  return updatedPermissionIds.sort((a, b) => a - b);
};
